import { ReactElement, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  CircularProgress,
  Typography
} from '@material-ui/core'
import { exchangeOAuthCode } from './api'
import { useAuth } from './AuthContext'

/**
 * Handles the OAuth callback redirect from Google/Microsoft.
 * Extracts the authorization code from the URL, exchanges it for tokens
 * via our self-hosted backend, and redirects to the app.
 */
const OAuthCallback = (): ReactElement => {
  const navigate = useNavigate()
  const { provider } = useParams<{ provider: string }>()
  const { setAuthFromCallback } = useAuth()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search)
      const code = params.get('code')
      const errorParam = params.get('error')

      if (errorParam) {
        setError(`Authentication was denied: ${errorParam}`)
        return
      }

      if (!code) {
        setError('No authorization code received')
        return
      }

      if (provider !== 'google' && provider !== 'microsoft') {
        setError('Unknown authentication provider')
        return
      }

      try {
        const { user, tokens } = await exchangeOAuthCode(provider, code)
        setAuthFromCallback(user, tokens)
        navigate('/info', { replace: true })
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Authentication failed'
        )
      }
    }

    void handleCallback()
  }, [provider, navigate, setAuthFromCallback])

  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        p={3}
      >
        <Typography variant="h5" gutterBottom color="error">
          Sign In Failed
        </Typography>
        <Typography color="textSecondary" style={{ marginBottom: 16 }}>
          {error}
        </Typography>
        <Typography
          variant="body2"
          color="primary"
          style={{ cursor: 'pointer' }}
          onClick={() => navigate('/login')}
        >
          Return to Sign In
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
    >
      <CircularProgress size={48} />
      <Typography variant="body1" style={{ marginTop: 24 }}>
        Signing you in...
      </Typography>
    </Box>
  )
}

export default OAuthCallback
